import { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, User, Mail, Phone, MapPin, ArrowLeft, Camera, Save, Upload, Navigation, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { isBookOwner } from '../lib/roles';
import { loadCheckoutResume } from '../lib/checkoutResume';

const EditProfilePage = () => {
  const { user, updateUser, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const profileImageRef = useRef(null);
  const qrCodeImageRef = useRef(null);
  const depositSectionRef = useRef(null);
  const topupAmountRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    pan_name: '',
    email: '',
    phone: '',
    house_flat_no: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    profile_image: null,
    profile_image_preview: '',
    upi_address: '',
    gpay_phonepe_number: '',
    qr_code_image: null,
    qr_code_preview: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [deposit, setDeposit] = useState(null);
  const [topupAmount, setTopupAmount] = useState(500);
  const [topupLoading, setTopupLoading] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  useEffect(() => {
    const loadDeposit = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/user/deposit`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDeposit(res.data);
      } catch (e) {
        setDeposit({ balance: 0, total_deposited: 0, total_deducted: 0 });
      }
    };
    loadDeposit();
  }, [token]);

  // Deep link from checkout / My Account → Add security deposit
  useEffect(() => {
    if (searchParams.get('addDeposit') !== '1') return;
    const resume = loadCheckoutResume();
    const shortfall = Math.ceil(Number(resume?.depositShortfall) || 0);
    if (shortfall > 0) setTopupAmount(shortfall);
    const t = setTimeout(() => {
      depositSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      topupAmountRef.current?.focus();
    }, 150);
    return () => clearTimeout(t);
  }, [searchParams]);

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handleDepositTopup = async () => {
    if (topupLoading) return;
    if (!topupAmount || Number(topupAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setTopupLoading(true);
    try {
      const res = await axios.post(
        `${API}/user/deposit/topup`,
        { amount: Number(topupAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway');
        return;
      }
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: res.data.amount,
        currency: 'INR',
        name: 'MyBook.Rent',
        description: 'Security deposit top-up',
        order_id: res.data.razorpay_order_id,
        handler: async (response) => {
          await axios.post(`${API}/user/deposit/verify`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            amount: res.data.amount,
          }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success(`₹${topupAmount} added to your security deposit`);
          const dep = await axios.get(`${API}/user/deposit`, { headers: { Authorization: `Bearer ${token}` } });
          setDeposit(dep.data);
          window.dispatchEvent(new Event('deposit-updated'));
          const resume = loadCheckoutResume();
          if (resume && searchParams.get('returnTo') === '/checkout') {
            navigate('/checkout', { state: resume, replace: true });
          }
        },
      };
      // Prefer key from backend if present in init response
      if (res.data.key_id) options.key = res.data.key_id;
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deposit top-up failed');
    } finally {
      setTopupLoading(false);
    }
  };

  const handleDepositWithdraw = async () => {
    if (withdrawLoading) return;
    const amount = Number(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if ((deposit?.active_rentals || 0) > 0) {
      toast.error('Return all active rentals before withdrawing your security deposit.');
      return;
    }
    setWithdrawLoading(true);
    try {
      const res = await axios.post(
        `${API}/user/deposit/withdraw`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `₹${amount} withdrawal requested`);
      const dep = await axios.get(`${API}/user/deposit`, { headers: { Authorization: `Bearer ${token}` } });
      setDeposit(dep.data);
      setWithdrawAmount('');
      window.dispatchEvent(new Event('deposit-updated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Save form data to localStorage to prevent data loss on refresh
  useEffect(() => {
    const savedForm = localStorage.getItem('editProfileFormDraft');
    if (savedForm && user) {
      try {
        const parsed = JSON.parse(savedForm);
        // Only use saved data if it's for the same user
        if (parsed._userId === user.id) {
          setFormData(prev => ({ ...prev, ...parsed, _userId: undefined }));
          return;
        }
      } catch (e) { /* ignore */ }
    }
    
    if (user) {
      // Prefer structured fields; fall back to splitting address once on first comma
      let house = '';
      let street = '';
      if (user.address) {
        const commaIdx = user.address.indexOf(', ');
        if (commaIdx >= 0) {
          house = user.address.slice(0, commaIdx);
          street = user.address.slice(commaIdx + 2);
        } else {
          house = user.address;
        }
      }
      setFormData({
        name: user.name || '',
        pan_name: user.pan_name || '',
        email: user.email || '',
        phone: user.phone || '',
        house_flat_no: house,
        street: street,
        city: user.city || '',
        state: user.state || '',
        pincode: user.pincode || '',
        profile_image: null,
        profile_image_preview: user.profile_image || '',
        upi_address: user.upi_address || '',
        gpay_phonepe_number: user.gpay_phonepe_number || '',
        qr_code_image: null,
        qr_code_preview: user.qr_code_image || ''
      });
    }
  }, [user]);

  // Auto-save form draft to localStorage
  useEffect(() => {
    if (user && formData.name) {
      const toSave = { ...formData, profile_image: null, qr_code_image: null, _userId: user.id };
      localStorage.setItem('editProfileFormDraft', JSON.stringify(toSave));
    }
  }, [formData, user]);

  const detectGPSLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          // Reverse geocode using free Nominatim API
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          const data = await response.json();
          if (data.address) {
            const addr = data.address;
            setFormData(prev => ({
              ...prev,
              house_flat_no: addr.house_number || prev.house_flat_no,
              street: addr.road || addr.neighbourhood || prev.street,
              city: addr.city || addr.town || addr.village || prev.city,
              state: addr.state || prev.state,
              pincode: addr.postcode || prev.pincode
            }));
            toast.success('Address auto-filled from your location!');
          }
        } catch (e) {
          toast.error('Could not auto-fill address. Please enter manually.');
        }
        setDetectingLocation(false);
      },
      (error) => {
        toast.error('Location access denied. Please enter address manually.');
        setDetectingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    
    // Apply specific filters
    if (name === 'name') {
      newValue = value.replace(/[^A-Za-z\s]/g, '');
    } else if (name === 'pincode' || name === 'phone') {
      newValue = value.replace(/\D/g, '');
    }
    
    setFormData({ ...formData, [name]: newValue });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!validTypes.includes(file.type)) {
        setErrors({ ...errors, profile_image: 'Only PNG/JPG/JPEG formats allowed' });
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        setErrors({ ...errors, profile_image: 'Image size must be less than 2MB' });
        toast.error('Profile image must be less than 2MB');
        return;
      }
      
      setFormData({ 
        ...formData, 
        profile_image: file,
        profile_image_preview: URL.createObjectURL(file)
      });
      setErrors({ ...errors, profile_image: '' });
    }
  };

  const handleQRCodeImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!validTypes.includes(file.type)) {
        setErrors({ ...errors, qr_code_image: 'Only PNG/JPG/JPEG formats allowed' });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, qr_code_image: 'Image size must be less than 5MB' });
        return;
      }
      
      setFormData({ 
        ...formData, 
        qr_code_image: file,
        qr_code_preview: URL.createObjectURL(file)
      });
      setErrors({ ...errors, qr_code_image: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const updateData = new FormData();
    updateData.append('name', formData.name);
    updateData.append('pan_name', formData.pan_name);
    updateData.append('email', formData.email);
    updateData.append('phone', formData.phone);
    updateData.append('address', `${formData.house_flat_no}, ${formData.street}`);
    updateData.append('city', formData.city);
    updateData.append('state', formData.state);
    updateData.append('pincode', formData.pincode);
    updateData.append('upi_address', formData.upi_address);
    updateData.append('gpay_phonepe_number', formData.gpay_phonepe_number);
    
    if (formData.profile_image) {
      updateData.append('profile_image', formData.profile_image);
    }
    
    if (formData.qr_code_image) {
      updateData.append('qr_code_image', formData.qr_code_image);
    }
    
    setLoading(true);
    try {
      const response = await axios.put(`${API}/auth/me`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refresh auth user without re-triggering welcome banner
      if (updateUser) {
        updateUser(response.data);
      }
      localStorage.removeItem('editProfileFormDraft');
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{color: 'var(--primary)'}}>Edit Profile</h1>
            <p className="text-gray-600">Update your personal information</p>
            <p className="text-xs text-gray-400 mt-1">Fields marked with * are mandatory</p>
          </div>

          {/* MBR-066: Security deposit visible on all profiles */}
          <div
            ref={depositSectionRef}
            className="mb-8 p-4 rounded-2xl border"
            style={{
              borderColor: searchParams.get('addDeposit') === '1' ? 'var(--primary)' : 'var(--border)',
              background: '#F7F4EC',
              boxShadow: searchParams.get('addDeposit') === '1' ? '0 0 0 2px var(--primary)' : undefined,
            }}
            data-testid="profile-deposit-section"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <IndianRupee className="h-4 w-4" /> Security Deposit
                </p>
                <p className="text-2xl font-bold" style={{color: 'var(--primary)'}}>
                  ₹{deposit?.available_balance ?? deposit?.balance ?? 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Wallet ₹{deposit?.balance ?? 0}
                  {(deposit?.held_amount || 0) > 0
                    ? ` · ₹${Math.round(deposit.held_amount)} held until the book is returned`
                    : ''}
                  {' '}· Deposited ₹{deposit?.total_deposited ?? 0} · Deducted ₹{deposit?.total_deducted ?? 0}
                </p>
              </div>
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <div className="flex items-center gap-2">
                  <Input
                    ref={topupAmountRef}
                    type="number"
                    min={1}
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(Number(e.target.value))}
                    className="w-28"
                    data-testid="profile-topup-amount"
                  />
                  <Button
                    type="button"
                    onClick={handleDepositTopup}
                    disabled={topupLoading}
                    style={{backgroundColor: 'var(--primary)', color: 'white'}}
                    data-testid="profile-deposit-topup"
                  >
                    {topupLoading ? '…' : 'Add Money'}
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={deposit?.balance || undefined}
                    placeholder="Withdraw"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="w-28"
                    disabled={(deposit?.active_rentals || 0) > 0 || !(deposit?.balance > 0)}
                    data-testid="profile-withdraw-amount"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDepositWithdraw}
                    disabled={
                      withdrawLoading
                      || !(deposit?.balance > 0)
                      || (deposit?.active_rentals || 0) > 0
                    }
                    data-testid="profile-deposit-withdraw"
                    title={
                      (deposit?.active_rentals || 0) > 0
                        ? 'Not available while you have active rentals'
                        : 'Withdraw security deposit'
                    }
                  >
                    {withdrawLoading ? '…' : 'Withdraw'}
                  </Button>
                </div>
                {(deposit?.active_rentals || 0) > 0 && (
                  <p className="text-xs text-amber-700">
                    Withdraw unavailable — you have {deposit.active_rentals} active rental(s).
                  </p>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Picture */}
            <div className="flex flex-col items-center">
              <div 
                className="w-32 h-32 border-2 border-dashed rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors overflow-hidden"
                style={{borderColor: 'var(--primary)'}}
                onClick={() => profileImageRef.current?.click()}
              >
                {formData.profile_image_preview ? (
                  <img 
                    src={formData.profile_image_preview} 
                    alt="Profile" 
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(formData.profile_image_preview, '_blank');
                    }}
                  />
                ) : (
                  <Camera className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <input
                type="file"
                ref={profileImageRef}
                onChange={handleProfileImageChange}
                accept="image/png,image/jpg,image/jpeg"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => profileImageRef.current?.click()}
                className="mt-4"
              >
                Change Profile Picture
              </Button>
              <p className="text-xs text-gray-500 mt-1">Max 2MB, PNG/JPG/JPEG</p>
              {errors.profile_image && <p className="text-red-500 text-sm mt-1">{errors.profile_image}</p>}
            </div>

            {/* Name */}
            <div>
              <Label>Full Name *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            {/* PAN Name */}
            <div>
              <Label>Name as per PAN *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  name="pan_name"
                  value={formData.pan_name}
                  onChange={handleChange}
                  placeholder="Enter name as on PAN card"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label>Email Address *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <Label>Phone Number *</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10-digit phone number"
                  required
                  maxLength={10}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Address *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={detectGPSLocation}
                  disabled={detectingLocation}
                >
                  <Navigation className="h-4 w-4 mr-1" />
                  {detectingLocation ? 'Detecting...' : 'Auto-fill from GPS'}
                </Button>
              </div>
              
              <Input
                name="house_flat_no"
                value={formData.house_flat_no}
                onChange={handleChange}
                placeholder="House/Flat No."
              />
              
              <Input
                name="street"
                value={formData.street}
                onChange={handleChange}
                placeholder="Street Address"
              />
              
              <div className="grid grid-cols-3 gap-3">
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                />
                <Input
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                />
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="PIN Code"
                    maxLength={6}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Payment Details Section */}
            {isBookOwner(user?.role) && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4" style={{color: 'var(--primary)'}}>Payment Details</h3>
                
                {/* UPI Address */}
                <div className="mb-4">
                  <Label>UPI Address</Label>
                  <div className="relative mt-1">
                    <Input
                      name="upi_address"
                      value={formData.upi_address}
                      onChange={handleChange}
                      placeholder="yourname@bank (e.g., name@paytm)"
                      className="pl-3"
                      style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                    />
                  </div>
                </div>

                {/* GPay/PhonePe Number */}
                <div className="mb-4">
                  <Label>GPay / PhonePe Mobile Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      name="gpay_phonepe_number"
                      type="tel"
                      value={formData.gpay_phonepe_number}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      maxLength={10}
                      className="pl-10"
                      style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                    />
                  </div>
                </div>

                {/* QR Code Upload */}
                <div className="mb-4">
                  <Label>UPI QR Code</Label>
                  <div className="mt-2 flex items-start space-x-4">
                    <div 
                      className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{borderColor: 'var(--primary)'}}
                      onClick={() => qrCodeImageRef.current?.click()}
                    >
                      {formData.qr_code_preview ? (
                        <img 
                          src={formData.qr_code_preview} 
                          alt="QR Code" 
                          className="w-full h-full object-cover rounded-lg cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(formData.qr_code_preview, '_blank');
                          }}
                        />
                      ) : (
                        <div className="text-center">
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs text-gray-500">Upload QR</p>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={qrCodeImageRef}
                        onChange={handleQRCodeImageChange}
                        accept="image/png,image/jpg,image/jpeg"
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => qrCodeImageRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload QR Code
                      </Button>
                      <p className="text-xs text-gray-500 mt-1">PNG/JPG/JPEG (Max 5MB)</p>
                      <p className="text-xs text-gray-500">Click QR image to enlarge</p>
                    </div>
                  </div>
                  {errors.qr_code_image && <p className="text-red-500 text-sm mt-1">{errors.qr_code_image}</p>}
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 hover:opacity-90"
              style={{backgroundColor: 'var(--primary)', color: 'white'}}
              disabled={loading}
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage;
